import React, { useState } from 'react';
import { Col, Row, getItemWithExpiration, formatAmountText } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, CaptionLight } from '../../lib/font';
import WhiteSquareButton from '../../components/Button/WhiteSquareButton';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import Modal from '../../components/modal/Modal';
import { useCenterMemberTickets, useUpdateMemberTicket, useRefundMemberTicket, useTickets } from '@/features/groupTicket/hooks';
import { MemberTicket, MemberTicketStatus, RefundMemberTicketBody, Ticket } from '@/features/groupTicket/types';
// barrel(features/sharedMembership) 대신 파일 직접 참조 — barrel은 ESM-only ky까지 즉시 평가한다.
import {
    clearStableSharedAccountingOperation,
    getStableSharedAccountingOperation,
    isReplayedAccountingResponse,
    REPLAYED_ACCOUNTING_ALERT_MESSAGE,
    REPLAYED_ACCOUNTING_ALERT_TITLE,
    StableSharedAccountingOperation,
} from '@/features/sharedMembership/accounting';
import dayjs from 'dayjs';

const STATUS_CONFIG: Record<MemberTicketStatus, { bg: string; color: string; label: string }> = {
    ACTIVE: { bg: '#ECFDF5', color: '#065F46', label: '활성' },
    EXPIRED: { bg: '#F3F4F6', color: '#6B7280', label: '만료' },
    SUSPENDED: { bg: '#FEF3C7', color: '#92400E', label: '정지' },
    REFUNDED: { bg: '#FEF2F2', color: '#991B1B', label: '환불' },
};

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
};

const GroupMembershipPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const [search, setSearch] = useState('');
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_groupMembership') !== 'hidden');
    const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
    const [page, setPage] = useState(1);

    const params: Record<string, string> = { page: String(page), limit: '20' };
    if (search) params.search = search;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

    const { data, isLoading } = useCenterMemberTickets({ centerId, params });
    const memberTickets: MemberTicket[] = data?.results ?? [];
    const totalResults = data?.totalResults ?? 0;
    const totalPages = Math.ceil(totalResults / 20);

    // 페이지 초과 시 리셋
    React.useEffect(() => {
        if (totalResults > 0 && page > totalPages) setPage(1);
    }, [totalResults, page, totalPages]);

    const [editTarget, setEditTarget] = useState<MemberTicket | null>(null);
    const [refundTarget, setRefundTarget] = useState<MemberTicket | null>(null);

    return (
        <Col style={{ width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' }}>
            <Col style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '40px 32px 80px' }}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Col>
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <Subtitle1Bold>그룹 수강권 현황</Subtitle1Bold>
                            {!showGuide && (
                                <button onClick={() => { setShowGuide(true); localStorage.removeItem('guide_groupMembership'); }}
                                    style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #C4B5FD', backgroundColor: '#F5F3FF', color: '#7C3AED', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                            )}
                        </Row>
                        <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                            그룹수업 수강권 발급/사용 현황을 관리합니다
                        </CaptionLight>
                    </Col>
                </Row>

                {showGuide && (
                    <Col style={{ marginTop: 16, padding: '18px 22px', backgroundColor: '#F5F3FF', borderRadius: 12, border: '1px solid #C4B5FD', position: 'relative' }}>
                        <button onClick={() => { setShowGuide(false); localStorage.setItem('guide_groupMembership', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#A78BFA', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#5B21B6', marginBottom: 6 }}>그룹 수강권 현황이란?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#5B21B6', lineHeight: 1.7 }}>
                            <span><b>그룹수업/프라이빗(1:2, 1:3)</b>에 발급된 수강권의 현황을 확인하는 페이지입니다.</span>
                            <span>1:1 PT 수강권은 상단 메뉴의 <b>'1:1 PT 수강권'</b>에서 별도로 관리됩니다.</span>
                        </Col>
                    </Col>
                )}

                {/* 필터 */}
                <Row style={{ gap: 12, marginTop: 24, alignItems: 'center' }}>
                    <input
                        value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder="회원명 또는 전화번호 검색"
                        style={{ ...inputStyle, maxWidth: 280 }}
                    />
                    <Row style={{ gap: 6 }}>
                        {(['ACTIVE', 'EXPIRED', 'REFUNDED', 'all'] as const).map((s) => {
                            const active = statusFilter === s;
                            const cfg = s === 'all' ? null : STATUS_CONFIG[s as MemberTicketStatus];
                            return (
                                <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                                    style={{
                                        padding: '6px 14px', borderRadius: 20, fontSize: 13,
                                        fontWeight: active ? 600 : 400,
                                        border: active ? `1.5px solid ${cfg?.color || '#374151'}` : '1px solid #E1E4E6',
                                        backgroundColor: active ? (cfg?.bg || '#F3F4F6') : 'white',
                                        color: active ? (cfg?.color || '#374151') : '#9CA3AF',
                                        cursor: 'pointer',
                                    }}>
                                    {s === 'all' ? '전체' : cfg?.label}
                                </button>
                            );
                        })}
                    </Row>
                    <span style={{ fontSize: 13, color: '#9CA3AF', marginLeft: 'auto' }}>
                        총 {totalResults}건
                    </span>
                </Row>

                {/* 목록 */}
                <div style={{ marginTop: 16, border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', backgroundColor: 'white' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#F9FAFB' }}>
                                <th style={thStyle}>회원</th>
                                <th style={thStyle}>수강권</th>
                                <th style={thStyle}>타입</th>
                                <th style={thStyle}>잔여/전체</th>
                                <th style={thStyle}>시작일</th>
                                <th style={thStyle}>만료일</th>
                                <th style={thStyle}>결제액</th>
                                <th style={thStyle}>상태</th>
                                <th style={{ ...thStyle, width: 100 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF', padding: 40 }}>불러오는 중...</td></tr>
                            )}
                            {!isLoading && memberTickets.length === 0 && (
                                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF', padding: 40 }}>발급된 수강권이 없습니다</td></tr>
                            )}
                            {memberTickets.map((mt) => {
                                const cfg = STATUS_CONFIG[mt.status] || STATUS_CONFIG.ACTIVE;
                                return (
                                    <tr key={mt._id} style={{ borderTop: '1px solid #F3F4F6' }}>
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>{(mt as any).memberName || mt.memberId}</td>
                                        <td style={tdStyle}>{mt.ticketName || '-'}</td>
                                        <td style={tdStyle}>
                                            <span style={{
                                                fontSize: 11, padding: '1px 6px', borderRadius: 3,
                                                backgroundColor: mt.type === 'COUNT' ? '#EFF6FF' : '#F5F3FF',
                                                color: mt.type === 'COUNT' ? '#1E40AF' : '#5B21B6',
                                            }}>{mt.type === 'COUNT' ? '횟수' : '기간'}</span>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{ fontWeight: 600, color: mt.remainingCount <= 2 ? '#EF4444' : '#374151' }}>
                                                {mt.remainingCount}
                                            </span>/{mt.totalSession ?? (mt.remainingCount + mt.usedCount)}
                                        </td>
                                        <td style={tdStyle}>{dayjs(mt.startDate).format('YY.MM.DD')}</td>
                                        <td style={tdStyle}>
                                            <span style={{ color: dayjs(mt.expiryDate).isBefore(dayjs().add(7, 'day')) ? '#EF4444' : '#374151' }}>
                                                {dayjs(mt.expiryDate).format('YY.MM.DD')}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>{(mt.totalPrice ?? mt.price ?? 0).toLocaleString()}원</td>
                                        <td style={tdStyle}>
                                            <span style={{
                                                fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                                backgroundColor: cfg.bg, color: cfg.color,
                                            }}>{cfg.label}</span>
                                        </td>
                                        <td style={tdStyle}>
                                            <Row style={{ gap: 6 }}>
                                                {mt.status === 'ACTIVE' && (
                                                    <>
                                                        <button onClick={() => setEditTarget(mt)} style={linkBtnStyle}>수정</button>
                                                        <button onClick={() => setRefundTarget(mt)} style={{ ...linkBtnStyle, color: '#EF4444' }}>환불</button>
                                                    </>
                                                )}
                                            </Row>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* 페이지네이션 */}
                {totalResults > 20 && (
                    <Row style={{ justifyContent: 'center', gap: 8, marginTop: 16 }}>
                        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                            style={{ ...pageBtnStyle, opacity: page <= 1 ? 0.4 : 1 }}>이전</button>
                        <span style={{ fontSize: 14, color: '#374151', padding: '0 8px' }}>{page} / {Math.ceil(totalResults / 20)}</span>
                        <button onClick={() => setPage(page + 1)} disabled={page >= Math.ceil(totalResults / 20)}
                            style={{ ...pageBtnStyle, opacity: page >= Math.ceil(totalResults / 20) ? 0.4 : 1 }}>다음</button>
                    </Row>
                )}
            </Col>

            {/* 수정 모달 */}
            {editTarget && (
                <EditMemberTicketModal
                    ticket={editTarget}
                    onClose={() => setEditTarget(null)}
                    createAlert={createAlert}
                    centerId={centerId}
                />
            )}

            {/* 환불 모달 */}
            {refundTarget && (
                <RefundModal
                    ticket={refundTarget}
                    onClose={() => setRefundTarget(null)}
                    createAlert={createAlert}
                    centerId={centerId}
                />
            )}
        </Col>
    );
};

/* ═══════ 수정 모달 ═══════ */

const EditMemberTicketModal = ({
    ticket, onClose, createAlert, centerId,
}: { ticket: MemberTicket; onClose: () => void; createAlert: any; centerId: string }) => {
    const updateMutation = useUpdateMemberTicket();
    const [expiryDate, setExpiryDate] = useState(dayjs(ticket.expiryDate).format('YYYY-MM-DD'));
    const [remainingCount, setRemainingCount] = useState(ticket.remainingCount);
    // 취소 정책 override — undefined = 템플릿 기본값 사용
    const [cancellableCount, setCancellableCount] = useState<number | undefined>(ticket.cancellableCount);
    const [sameDayCancelCount, setSameDayCancelCount] = useState<number | undefined>(ticket.sameDayCancelCount);

    // 템플릿 기본값 참조
    const { data: products = [] } = useTickets({ centerId });
    const ticketProduct = (products as Ticket[]).find((t) => {
        const tid = typeof (ticket as any).ticketId === 'object' ? (ticket as any).ticketId?._id : ticket.ticketId;
        return t._id === tid;
    });
    const templateCancel = ticketProduct?.cancellableCount;
    const templateSameDay = ticketProduct?.sameDayCancelCount;

    const handleSave = async () => {
        // validation: 당일 취소 ≤ 총 취소
        if (cancellableCount != null && sameDayCancelCount != null && sameDayCancelCount > cancellableCount) {
            createAlert('입력 오류', '당일 취소 횟수는 총 취소 횟수보다 클 수 없습니다.', true);
            return;
        }
        try {
            await updateMutation.mutateAsync({
                centerId, memberTicketId: ticket._id, memberId: ticket.memberId,
                body: {
                    expiryDate, remainingCount,
                    // undefined 전송 시 백엔드가 해당 필드 변경 안 함 — null로 보내면 명시적 clear
                    // UI에선 빈 값 = 템플릿 기본값 복귀이므로 null 전송
                    cancellableCount: cancellableCount ?? null,
                    sameDayCancelCount: sameDayCancelCount ?? null,
                } as any,
            });
            createAlert('수정 완료', '수강권이 수정되었습니다.', false);
            onClose();
        } catch { createAlert('수정 실패', '수강권 수정에 실패했습니다.', true); }
    };

    return (
        <Modal isCloseButton setIsVisible={onClose} whiteViewWidth={440}>
            <Col style={{ padding: 32, gap: 16 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>수강권 수정</span>
                <span style={{ fontSize: 14, color: '#6B7280' }}>{ticket.ticketName} — {(ticket as any).memberName || ticket.memberId}</span>
                <Col style={{ gap: 4 }}>
                    <Body2Regular color={colors.LAVEL_2}>만료일</Body2Regular>
                    <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} style={inputStyle} />
                </Col>
                <Col style={{ gap: 4 }}>
                    <Body2Regular color={colors.LAVEL_2}>잔여 횟수</Body2Regular>
                    <input type="number" value={remainingCount} onChange={(e) => setRemainingCount(Number(e.target.value))} min={0} style={inputStyle} />
                </Col>

                {/* 취소 정책 override */}
                <Col style={{ gap: 6, paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
                    <Row style={{ alignItems: 'center', gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>취소 정책 (회원별 설정)</Body2Regular>
                    </Row>
                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                        비우면 템플릿 기본값 적용 · 기본 총 {templateCancel ? `${templateCancel}회` : '무제한'} / 당일 {templateSameDay ? `${templateSameDay}회` : '무제한'}
                    </span>
                    <Row style={{ gap: 10 }}>
                        <Col style={{ flex: 1, gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>총 취소 허용</span>
                            <input
                                type="number" min={0}
                                value={cancellableCount ?? ''}
                                placeholder={templateCancel != null ? `기본 ${templateCancel}` : '기본값'}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setCancellableCount(v === '' ? undefined : Math.max(0, Number(v)));
                                }}
                                style={inputStyle}
                            />
                        </Col>
                        <Col style={{ flex: 1, gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>당일 취소 허용</span>
                            <input
                                type="number" min={0}
                                value={sameDayCancelCount ?? ''}
                                placeholder={templateSameDay != null ? `기본 ${templateSameDay}` : '기본값'}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setSameDayCancelCount(v === '' ? undefined : Math.max(0, Number(v)));
                                }}
                                style={inputStyle}
                            />
                        </Col>
                    </Row>
                    {/* 현재 적용 상태 표시 */}
                    {(cancellableCount != null || sameDayCancelCount != null) && (
                        <span style={{ fontSize: 11, color: '#065F46', backgroundColor: '#ECFDF5', padding: '4px 8px', borderRadius: 4, alignSelf: 'flex-start' }}>
                            회원 커스텀 적용 중
                        </span>
                    )}
                    {cancellableCount != null && sameDayCancelCount != null && sameDayCancelCount > cancellableCount && (
                        <span style={{ fontSize: 12, color: '#B91C1C' }}>⚠ 당일 취소 횟수는 총 취소 횟수보다 클 수 없어요</span>
                    )}
                </Col>

                <Row style={{ gap: 8, justifyContent: 'flex-end' }}>
                    <WhiteSquareButton text="취소" size="small" callBack={onClose} />
                    <ColorSquareButton text="저장" size="small" callBack={handleSave} isDisabled={updateMutation.isLoading} />
                </Row>
            </Col>
        </Modal>
    );
};

/* ═══════ 환불 모달 ═══════ */

const RefundModal = ({
    ticket, onClose, createAlert, centerId,
}: { ticket: MemberTicket; onClose: () => void; createAlert: any; centerId: string }) => {
    const refundMutation = useRefundMemberTicket();
    const [refundAmount, setRefundAmount] = useState(0);
    /**
     * "빈칸"은 금액 숫자만으로 표현할 수 없다 — `Number('') === 0` 이라 0원과 구분되지 않는다.
     * 지운 시점의 값을 기억해 두고 그 값이 그대로일 때만 빈칸으로 보여준다.
     * 부모·서버가 값을 갈아끼우면 sentinel 이 어긋나 실제 값이 저절로 다시 보인다.
     */
    const [refundAmountBlankAt, setRefundAmountBlankAt] = useState<number | null>(null);
    const isRefundAmountBlank = refundAmountBlankAt === refundAmount;
    const amountCaret = useAmountInputCaret({ allowDecimal: true });
    const [refundReason, setRefundReason] = useState('');

    const handleRefundAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // 원칙 1 — 패턴에 안 맞으면 거부한다. 비숫자를 걷어내지 않는다.
        const digits = amountCaret.changeAmount(e, isRefundAmountBlank ? '' : refundAmount);
        if (digits == null) return;
        // 원칙 2 — state 에는 콤마 없는 값만 담는다.
        const next = Number(digits);
        setRefundAmount(next);
        // 빈칸은 0원으로 두어 아래 `refundAmount <= 0` 이 그대로 제출을 막게 한다.
        setRefundAmountBlankAt(digits === '' ? next : null);
    };
    // 응답을 못 받은 재시도는 같은 키를 다시 보내야 금액이 두 번 깎이지 않는다.
    const refundOperationRef = React.useRef<StableSharedAccountingOperation | null>(null);
    /**
     * 이 모달을 연 시점의 일자로 고정한다. 클릭할 때마다 다시 계산하면 자정을 넘긴 재시도가
     * 다른 키를 만들어 멱등성이 사라진다(= 이중 환불).
     */
    const [refundIntentDate] = useState(() => dayjs().startOf('day').toISOString());

    const handleRefund = async () => {
        // `NaN <= 0` 은 false 라 이 검사만으로는 NaN 이 그대로 서버로 간다. finite 를 먼저 본다.
        if (!Number.isFinite(refundAmount) || refundAmount <= 0) { createAlert('입력 오류', '환불 금액을 입력해주세요.', true); return; }
        // 사유는 재시도 중 오타를 고칠 수 있으므로 키 구성에서 제외한다.
        // v1 환불은 회차 입력이 없으므로 회차는 0으로 둔다.
        const refundOperation = getStableSharedAccountingOperation(refundOperationRef.current, {
            type: 'REFUND',
            membershipId: ticket._id,
            session: 0,
            amount: refundAmount,
            paymentDate: refundIntentDate,
        });
        refundOperationRef.current = refundOperation;
        try {
            const result = await refundMutation.mutateAsync({
                centerId, memberTicketId: ticket._id, memberId: ticket.memberId,
                body: {
                    refundAmount,
                    refundReason: refundReason || undefined,
                    clientOperationId: refundOperation.operationId,
                } as RefundMemberTicketBody,
            });
            // 성공을 확인한 operation만 폐기한다. 실패·응답 유실 때는 남겨 재시도가 같은 키를 쓰게 한다.
            clearStableSharedAccountingOperation(refundOperation);
            refundOperationRef.current = null;
            // 서버가 최초 결과를 재생했다면 이번 요청으로 돈이 움직이지 않았다.
            // "환불 완료"로 보여주면 관리자가 의도한 2번째 환불이 조용히 삼켜진다.
            if (isReplayedAccountingResponse(result)) {
                createAlert(REPLAYED_ACCOUNTING_ALERT_TITLE, REPLAYED_ACCOUNTING_ALERT_MESSAGE, true);
            } else {
                createAlert('환불 완료', '수강권이 환불되었습니다.', false);
            }
            onClose();
        } catch {
            // v1 환불은 서버가 아직 요청 번호로 중복을 막아주지 않는다(BACKEND_HANDOFF_REFUND_IDEMPOTENCY.md).
            // 중복 방지를 약속하지 말고 확인부터 안내한다.
            createAlert(
                '환불 실패',
                '환불에 실패했습니다. 목록을 새로고침해 처리 여부를 확인한 뒤 같은 금액으로 다시 시도해주세요.',
                true,
            );
        }
    };

    return (
        <Modal isCloseButton setIsVisible={onClose} whiteViewWidth={400}>
            <Col style={{ padding: 32, gap: 16 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>수강권 환불</span>
                <span style={{ fontSize: 14, color: '#6B7280' }}>{ticket.ticketName} — 결제액 {(ticket.totalPrice ?? ticket.price ?? 0).toLocaleString()}원</span>
                <Col style={{ gap: 4 }}>
                    <Body2Regular color={colors.LAVEL_2}>환불 금액 (원)</Body2Regular>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={isRefundAmountBlank ? '' : formatAmountText(String(refundAmount))}
                        onChange={handleRefundAmountChange}
                        // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다.
                        // `10000,20000` 같은 CSV 조각을 흘려보내면 콤마를 걷어낸 다른 금액이 된다.
                        onPaste={amountCaret.pasteAmount}
                        onDrop={amountCaret.dropAmount}
                        onBlur={() => setRefundAmountBlankAt(null)}
                        style={inputStyle}
                    />
                </Col>
                <Col style={{ gap: 4 }}>
                    <Body2Regular color={colors.LAVEL_2}>환불 사유</Body2Regular>
                    <input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="선택 입력" style={inputStyle} />
                </Col>
                <Row style={{ gap: 8, justifyContent: 'flex-end' }}>
                    <WhiteSquareButton text="취소" size="small" callBack={onClose} />
                    <ColorSquareButton text="환불" size="small" callBack={handleRefund} isDisabled={refundMutation.isLoading} />
                </Row>
            </Col>
        </Modal>
    );
};

const thStyle: React.CSSProperties = { padding: '10px 14px', textAlign: 'left', fontWeight: 500, color: '#6B7280', fontSize: 13 };
const tdStyle: React.CSSProperties = { padding: '10px 14px', color: '#374151', fontSize: 14 };
const linkBtnStyle: React.CSSProperties = { fontSize: 12, color: '#3B82F6', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' };
const pageBtnStyle: React.CSSProperties = { padding: '6px 16px', borderRadius: 8, border: '1px solid #E1E4E6', backgroundColor: 'white', fontSize: 13, cursor: 'pointer' };

export default GroupMembershipPage;
